import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

// Styled Components
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: white;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  table-layout: fixed;
`;

const TableHeader = styled.thead`
  background: #f8fafc;
  border-bottom: 2px solid #e5e7eb;
`;

const TableBody = styled.tbody`
  tr:nth-of-type(even) {
    background: #f8f9fa;
  }
  
  tr:hover {
    background: #e9ecef;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s ease;
`;

const TableCell = styled.td`
  padding: 0;
  text-align: ${props => props.align || 'left'};
  vertical-align: middle;
  border-right: 1px solid #dee2e6;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 0;
  background-color: ${props => props.backgroundColor || 'transparent'};
  
  &:last-child {
    border-right: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-weight: 600;
  color: #495057;
  border-right: 1px solid #dee2e6;
  cursor: ${props => props.sortable ? 'pointer' : 'default'};
  user-select: none;
  position: relative;
  width: ${props => props.width || '150px'};
  min-width: 100px;
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => props.sortable ? '#e9ecef' : 'transparent'};
  }
  
  /* Resize handle */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    z-index: 1;
  }
  
  &:hover::after {
    background: #007bff;
    opacity: 0.3;
  }
`;

const SortIcon = styled.span`
  margin-left: 8px;
  color: #6c757d;
  font-size: 12px;
  
  ${props => props.active && css`
    color: #007bff;
  `}
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
`;

const PaginationInfo = styled.span`
  color: #6c757d;
  font-size: 14px;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  ${props => props.active && css`
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  `}
`;

const PageSizeSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background: white;
  color: #495057;
  font-size: 14px;
  cursor: pointer;
`;

const SearchContainer = styled.div`
  padding: 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 16px;
`;

const ScrollPalette = styled.div`
  position: fixed;
  bottom: 8px;
  right: 20px;
  background: white;
  border: 1px solid #dadce0;
  border-radius: 8px;
  box-shadow: none;
  padding: 2px;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  visibility: ${props => props.visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease;
  width: 120px;
  height: 19px;
  display: flex;
  align-items: center;
`;

const ScrollSlider = styled.input`
  width: 100%;
  height: 15px;
  background: transparent;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  
  &::-webkit-slider-track {
    -webkit-appearance: none;
    appearance: none;
    height: 15px;
    background: #f1f3f4;
    border-radius: 8px;
    border: 1px solid #dadce0;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 64px;
    height: 10px;
    border-radius: 5px;
    background: #636363 !important;
    cursor: pointer;
    border: none;
    box-shadow: none;
    margin-top: 0px;
    position: relative;
    
    &:hover {
      background: #9aa0a6 !important;
    }
    
    &:active {
      background: #6b7280 !important;
    }
  }
  
  &::-moz-range-track {
    height: 15px;
    background: #f1f3f4;
    border-radius: 8px;
    border: 1px solid #dadce0;
    border: none;
  }
  
  &::-moz-range-thumb {
    width: 64px;
    height: 10px;
    border-radius: 5px;
    background: #636363 !important;
    cursor: pointer;
    border: none;
    box-shadow: none;
    
    &:hover {
      background: #9aa0a6 !important;
    }
    
    &:active {
      background: #6b7280 !important;
    }
  }
`;


// Main Table Component
const DataTable = ({
  data = [],
  columns = [],
  pageSize = 10,
  searchable = true,
  sortable = true,
  pagination = true,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  rowKey = 'loan_id',
  onRowClick,
  className,
  ...props
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [showScrollPalette, setShowScrollPalette] = useState(false);
  const [maxScrollLeft, setMaxScrollLeft] = useState(0);
  const [sliderValue, setSliderValue] = useState(0);
  const tableContainerRef = useRef(null);

  // Handle selection - will be defined after paginatedData is available

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    return data.filter(row =>
      columns.some(column => {
        const value = row[column.key];
        return value && value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    
    const startIndex = (currentPage - 1) * currentPageSize;
    const endIndex = startIndex + currentPageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, currentPageSize, pagination]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedData.length / currentPageSize);
  const startItem = (currentPage - 1) * currentPageSize + 1;
  const endItem = Math.min(currentPage * currentPageSize, sortedData.length);

  // Handle sorting
  const handleSort = (key) => {
    if (!sortable) return;
    
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setCurrentPageSize(parseInt(newPageSize));
    setCurrentPage(1);
  };



  // Reset pagination when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data, searchTerm]);

  // Scroll palette logic
  useEffect(() => {
    const updateScrollPalette = () => {
      if (!tableContainerRef.current) return;
      
      const tableContainer = tableContainerRef.current;
      const table = tableContainer.querySelector('table');
      
      if (table) {
        const containerWidth = tableContainer.offsetWidth;
        const tableWidth = table.scrollWidth;
        const hasHorizontalScroll = tableWidth > containerWidth;
        
        // Check if the table's scrollbar is visible in the viewport
        const tableRect = tableContainer.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const isScrollbarVisible = tableRect.bottom <= viewportHeight - 20; // 20px buffer
        
        // Only show palette if there's horizontal scroll AND the table scrollbar isn't visible
        setShowScrollPalette(hasHorizontalScroll && !isScrollbarVisible);
        setMaxScrollLeft(tableWidth - containerWidth);
      }
    };

    const handleTableScroll = () => {
      // Update slider position based on current scroll
      if (tableContainerRef.current && maxScrollLeft > 0) {
        const currentScrollLeft = tableContainerRef.current.scrollLeft;
        const newSliderValue = Math.round((currentScrollLeft / maxScrollLeft) * 100);
        setSliderValue(newSliderValue);
      }
    };

    // Initial check
    updateScrollPalette();
    
    // Add event listeners
    if (tableContainerRef.current) {
      tableContainerRef.current.addEventListener('scroll', handleTableScroll);
    }
    window.addEventListener('resize', updateScrollPalette);
    window.addEventListener('scroll', updateScrollPalette); // Check when page scrolls

    return () => {
      if (tableContainerRef.current) {
        tableContainerRef.current.removeEventListener('scroll', handleTableScroll);
      }
      window.removeEventListener('resize', updateScrollPalette);
      window.removeEventListener('scroll', updateScrollPalette);
    };
  }, [paginatedData, columns]);

  // Handle slider change
  const handleSliderChange = (e) => {
    if (!tableContainerRef.current) return;
    
    const newSliderValue = parseInt(e.target.value);
    setSliderValue(newSliderValue);
    const scrollLeft = (newSliderValue / 100) * maxScrollLeft;
    tableContainerRef.current.scrollLeft = scrollLeft;
  };

  // Column resize handlers
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startWidth = columnWidths[columnKey] || columns.find(col => col.key === columnKey)?.width || '150px';
    const startWidthNum = parseInt(startWidth);
    
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(100, startWidthNum + diff); // Minimum width of 100px
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: `${newWidth}px`
      }));
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };





  // Selection handlers (defined after paginatedData)
  const handleSelectAll = (e) => {
    if (!selectable || !onSelectionChange) return;
    
    if (e.target.checked) {
      // Select all rows on current page
      const pageRowKeys = paginatedData.map(row => row[rowKey]);
      const newSelection = [...new Set([...selectedRows, ...pageRowKeys])];
      onSelectionChange(newSelection);
    } else {
      // Deselect all rows on current page
      const pageRowKeys = paginatedData.map(row => row[rowKey]);
      const newSelection = selectedRows.filter(key => !pageRowKeys.includes(key));
      onSelectionChange(newSelection);
    }
  };

  const handleRowSelect = (rowKeyValue, e) => {
    e.stopPropagation(); // Prevent row click when clicking checkbox
    if (!selectable || !onSelectionChange) return;
    
    if (selectedRows.includes(rowKeyValue)) {
      onSelectionChange(selectedRows.filter(key => key !== rowKeyValue));
    } else {
      onSelectionChange([...selectedRows, rowKeyValue]);
    }
  };

  const isAllPageSelected = selectable && paginatedData.length > 0 && 
    paginatedData.every(row => selectedRows.includes(row[rowKey]));

  // Render sort icon
  const renderSortIcon = (columnKey) => {
    if (!sortable) return null;
    
    if (sortConfig.key === columnKey) {
      return <SortIcon active>{sortConfig.direction === 'asc' ? '↑' : '↓'}</SortIcon>;
    }
    return <SortIcon>↕</SortIcon>;
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  return (
    <>
      <TableContainer ref={tableContainerRef} className={className} {...props}>
      {searchable && (
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
      )}
      
      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHeaderCell width="50px" align="center">
                <input
                  type="checkbox"
                  checked={isAllPageSelected}
                  onChange={handleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </TableHeaderCell>
            )}
            {columns.map((column) => (
              <TableHeaderCell
                key={column.key}
                align={column.align}
                sortable={sortable && column.sortable !== false}
                width={columnWidths[column.key] || column.width || '150px'}
                onClick={() => handleSort(column.key)}
                title={column.title}
              >
                {column.title}
                {renderSortIcon(column.key)}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '4px',
                    height: '100%',
                    cursor: 'col-resize',
                    zIndex: 2
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleMouseDown(e, column.key);
                  }}
                />
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        
        <TableBody>
          {paginatedData.length > 0 ? (
            paginatedData.map((row, index) => (
              <TableRow
                key={index}
                onClick={() => onRowClick && onRowClick(row, index)}
                style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                data-cy="case-row"
              >
                {selectable && (
                  <TableCell align="center">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row[rowKey])}
                      onChange={(e) => handleRowSelect(row[rowKey], e)}
                      style={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell 
                    key={column.key} 
                    align={column.align}
                    backgroundColor={column.getBackgroundColor ? column.getBackgroundColor(row[column.key], row, index) : undefined}
                    title={typeof row[column.key] === 'string' && row[column.key].length > 50 ? row[column.key] : undefined}
                    style={{ width: columnWidths[column.key] || column.width || '150px' }}
                  >
                    {column.render ? column.render(row[column.key], row, index) : row[column.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={selectable ? columns.length + 1 : columns.length}>
                <EmptyState>No data available</EmptyState>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {pagination && sortedData.length > 0 && totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Showing {startItem} to {endItem} of {sortedData.length} entries
          </PaginationInfo>
          
          <PaginationControls>
            <PageSizeSelect
              value={currentPageSize}
              onChange={(e) => handlePageSizeChange(e.target.value)}
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </PageSizeSelect>
            
            <PaginationButton
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            >
              First
            </PaginationButton>
            
            <PaginationButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </PaginationButton>
            
            {getPageNumbers().map((page) => (
              <PaginationButton
                key={page}
                onClick={() => handlePageChange(page)}
                active={currentPage === page}
              >
                {page}
              </PaginationButton>
            ))}
            
            <PaginationButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </PaginationButton>
            
            <PaginationButton
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </PaginationButton>
          </PaginationControls>
        </PaginationContainer>
      )}
      </TableContainer>
      
      {/* Scroll palette */}
      <ScrollPalette visible={showScrollPalette}>
        <ScrollSlider
          type="range"
          min="0"
          max="100"
          value={sliderValue}
          onChange={handleSliderChange}
        />
      </ScrollPalette>
    </>
  );
};

export default DataTable;
